import {
  Button,
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  cn,
} from "@atlas/core";
import { FilterBuilder, useSessionData, useTranslation } from "@atlas/sdk";
import type { IHubPackage } from "@models";
import { useHubCurrentCart, useSearchHubPackages } from "@query";
import { useSwitchPackage } from "@shared/hooks";
import { X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

export interface IPackageSwitcherProps {
  currentPackageId?: number | null;
  className?: string;
  onCancel?: () => void;
  onSuccess?: (pkg: IHubPackage) => void;
}

export function PackageSwitcher({
  currentPackageId,
  className,
  onCancel,
  onSuccess,
}: IPackageSwitcherProps) {
  const { t } = useTranslation();
  const { userId } = useSessionData();
  const uid = (userId ?? "").trim();
  const { data: userHubCart } = useHubCurrentCart(uid, {
    enabled: uid.length > 0,
  });

  const { data: packagesData } = useSearchHubPackages({
    filter: new FilterBuilder().notEquals("status", "Installed1").build(),
    sort: ["modifiedByDatetime DESC"],
    mode: "DATA",
    startIndex: 0,
    limit: 100,
  });

  const packagesOptions = useMemo(() => {
    const items =
      packagesData?.pages.flatMap(
        (page: { items: IHubPackage[] }) => page.items,
      ) ?? [];
    return items.map((pkg: IHubPackage) => {
      const label =
        (pkg.label || pkg.name || "").trim() ||
        t("hub.header.cart_package_fallback", { defaultValue: "Package" });
      return {
        value: pkg.packageId?.toString() ?? "",
        label: label,
      };
    });
  }, [packagesData, t]);

  const { switchPackage } = useSwitchPackage(userHubCart);

  const handleSwitchPackage = useCallback(
    (selectedOption: { value: string; label: string } | null) => {
      if (!selectedOption) return;
      const pkgId = parseInt(selectedOption.value, 10);
      if (Number.isNaN(pkgId) || pkgId <= 0) return;

      const pkg = packagesData?.pages
        .flatMap((page: { items: IHubPackage[] }) => page.items)
        .find((p: IHubPackage) => p.packageId === pkgId);

      if (pkg) {
        switchPackage(pkg, {
          onSuccess: () => {
            onSuccess?.(pkg);
          },
        });
      }
    },
    [packagesData, switchPackage, onSuccess],
  );

  const selectedValue =
    currentPackageId?.toString() ?? userHubCart?.packageId?.toString();

  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  return (
    <div
      className={cn("flex w-full min-w-0 items-center gap-2", className)}
      ref={setContainer}>
      <div className="flex-1 min-w-0 max-w-75">
        <div className="mt-2">
          <Combobox
            items={packagesOptions}
            value={
              packagesOptions.find(
                (o: { value: string; label: string }) =>
                  o.value === selectedValue,
              ) ?? null
            }
            onValueChange={(v: unknown) =>
              handleSwitchPackage(v as { value: string; label: string } | null)
            }>
            <ComboboxInput
              placeholder={t("hub.package_manager.search_placeholder", {
                defaultValue: "Search packages...",
              })}
            />
            <ComboboxContent container={container} className="max-w-100 z-1000">
              <ComboboxEmpty>
                {t("hub.package_manager.no_packages", {
                  defaultValue: "No packages found.",
                })}
              </ComboboxEmpty>
              <ComboboxList>
                <ComboboxCollection>
                  {(item: { value: string; label: string }) => (
                    <ComboboxItem value={item}>{item.label}</ComboboxItem>
                  )}
                </ComboboxCollection>
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        </div>
      </div>
      {onCancel && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 shrink-0"
          onClick={onCancel}
          aria-label={t("hub.administer.cancel", {
            defaultValue: "Cancel",
          })}>
          <X className="size-4" aria-hidden />
        </Button>
      )}
    </div>
  );
}
